import { ActionIcon, Badge, Group, Loader, Stack, Table, Text, Tooltip } from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";

import { useMeterAggregates } from "@/api/queries";
import type { DeviceMeter, LatestReading, Meter } from "@/api/types";
import { Sparkline } from "@/components/Sparkline";

export type MeterReadingsTableProps = {
  meters: MeterTableRow[];
  siteSlug: string | undefined;
  showLatest?: boolean;
  /**
   * Optional per-row Edit affordance. When provided, an icon button is
   * rendered in a trailing actions column. Callers gate this on RBAC
   * (e.g. only attach the callback when the principal has the `admin`
   * role) — this component just renders what it's given.
   */
  onEditMeter?: (meter: MeterTableRow) => void;
};

export type MeterTableRow = Pick<
  Meter | DeviceMeter,
  "id" | "slug" | "display_name" | "kind" | "unit" | "status"
> & {
  latest?: LatestReading | null;
};

export function MeterReadingsTable({
  meters,
  siteSlug,
  showLatest = false,
  onEditMeter,
}: MeterReadingsTableProps) {
  return (
    <Table highlightOnHover verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Kind</Table.Th>
          <Table.Th>Unit</Table.Th>
          {showLatest && <Table.Th>Latest</Table.Th>}
          <Table.Th>Last 24h</Table.Th>
          <Table.Th>Status</Table.Th>
          {onEditMeter && <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {meters.map((meter) => (
          <MeterRow
            key={meter.id}
            meter={meter}
            siteSlug={siteSlug}
            showLatest={showLatest}
            onEditMeter={onEditMeter}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
}

type MeterRowProps = {
  meter: MeterTableRow;
  siteSlug: string | undefined;
  showLatest: boolean;
  onEditMeter?: (meter: MeterTableRow) => void;
};

function MeterRow({ meter, siteSlug, showLatest, onEditMeter }: MeterRowProps) {
  // 15-minute buckets over 24 hours == 96 points, plenty of resolution
  // for a strip sparkline and cheap for the backend.
  const aggregates = useMeterAggregates(siteSlug, meter.slug, { interval: "15m" });

  const buckets = aggregates.data?.items ?? [];
  const populated = buckets.filter((b) => b.avg != null);
  const latest = populated[populated.length - 1]?.avg ?? null;

  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={0}>
          <Text fw={500}>{meter.display_name}</Text>
          <Text c="dimmed" size="xs">
            {meter.slug}
          </Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Badge variant="default" tt="lowercase">
          {meter.kind}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text ff="monospace" size="sm">
          {meter.unit}
        </Text>
      </Table.Td>
      {showLatest && (
        <Table.Td>
          <LatestValue latest={meter.latest ?? null} unit={meter.unit} />
        </Table.Td>
      )}
      <Table.Td>
        {aggregates.isLoading ? (
          <Loader size="xs" />
        ) : aggregates.error ? (
          <Text c="dimmed" size="xs">
            n/a
          </Text>
        ) : (
          <Group gap="xs" wrap="nowrap">
            <Tooltip
              label={
                latest != null
                  ? `Latest bucket average: ${latest.toFixed(2)} ${meter.unit}`
                  : "No readings in the last 24h"
              }
              withArrow
            >
              <span>
                <Sparkline
                  buckets={buckets}
                  width={120}
                  height={28}
                  label={`${meter.display_name} last 24h`}
                />
              </span>
            </Tooltip>
            {latest != null && (
              <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                {latest.toFixed(2)} {meter.unit}
              </Text>
            )}
          </Group>
        )}
      </Table.Td>
      <Table.Td>
        <Badge
          variant="light"
          color={meter.status === "active" ? "green" : "gray"}
        >
          {meter.status}
        </Badge>
      </Table.Td>
      {onEditMeter && (
        <Table.Td style={{ textAlign: "right" }}>
          <Tooltip label="Edit meter">
            <ActionIcon
              variant="subtle"
              aria-label={`Edit ${meter.display_name}`}
              onClick={() => onEditMeter(meter)}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
      )}
    </Table.Tr>
  );
}

function LatestValue({
  latest,
  unit,
}: {
  latest: LatestReading | null;
  unit: string;
}) {
  if (!latest) {
    return (
      <Text c="dimmed" size="sm">
        no data yet
      </Text>
    );
  }

  if (latest.value === null || !Number.isFinite(latest.value)) {
    return (
      <Stack gap={0}>
        <Text c="dimmed" size="sm">
          no value yet
        </Text>
        <Text c="dimmed" size="xs">
          {latest.quality}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap={0}>
      <Text size="sm" fw={500}>
        {latest.value.toFixed(2)} {unit}
      </Text>
      <Text c="dimmed" size="xs">
        {latest.quality}
      </Text>
    </Stack>
  );
}
